<template>
  <section
    class="sf-modal"
    :class="[staticClass, className]"
  >
    <SfOverlay
      v-if="overlay"
      class="sf-modal__overlay"
      :transition="transitionOverlay"
      :visible="visible"
    />
    <transition :name="transitionModal">
      <div
        v-if="visible"
        v-focus-trap
        v-click-outside="checkPersistence"
        class="sf-modal__container rounded w-full flex flex-col gap-4"
        :class="[
          whiteBackground ? 'bg-EXPONDO-white' : 'bg-EXPONDO-grey-5',
          narrow ? 'max-w-screen-sm' : 'max-w-screen-lg'
        ]"
      >
        <slot name="modal-bar">
          <div class="w-full flex justify-end pt-4 px-4">
            <CloseButton
              @click="close"
            />
          </div>
        </slot>
        <slot
          v-if="title"
          name="modal-title"
        >
          <div class="text-2xl font-bold text-center px-4">
            {{ title }}
          </div>
        </slot>
        <div
          ref="content"
          class="pb-8 flex-1"
          :class="[
            noMobilePadding ? 'px-0 lg:px-4' : 'px-4',
            noContentScroll ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'
          ]"
        >
          <slot />
        </div>
      </div>
    </transition>
  </section>
</template>

<script lang="ts">

import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { focusTrap } from '~/directives/focus-trap/focus-trap-directive';
import { clickOutside } from '~/directives/click-outside/click-outside-directive';
import isClient from '~~/helpers/isClient/isClient';

export default defineComponent({
  name: 'SfModal',
  directives: { focusTrap, clickOutside },

  model: {
    prop: 'visible',
    event: 'close',
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    cross: {
      type: Boolean,
      default: true,
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    transitionOverlay: {
      type: String,
      default: 'sf-fade',
    },
    transitionModal: {
      type: String,
      default: 'sf-fade',
    },
    whiteBackground: {
      type: Boolean,
      default: false,
    },
    noMobilePadding: {
      type: Boolean,
      default: false,
    },
    narrow: {
      type: Boolean,
      default: false,
    },
    noContentScroll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {

    return {
      staticClass: null,
      className: null,
    };

  },
  watch: {
    visible: {
      handler (value) {

        if (!isClient()) {

          return;

        }

        if (value) {

          this.$nextTick(() => {

            disableBodyScroll(this.$refs.content);

          });
          document.addEventListener('keydown', this.keydownHandler);

        } else {

          clearAllBodyScrollLocks();
          document.removeEventListener('keydown', this.keydownHandler);

        }

      },
      immediate: true,
    },
  },
  beforeUnmount() {

    clearAllBodyScrollLocks();

  },
  methods: {
    close() {

      this.$emit('close', false);

    },
    checkPersistence() {

      if (!this.persistent) {

        this.close();

      }

    },
    keydownHandler(e) {

      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {

        this.close();

      }

    },
    classHandler() {

      if (this.staticClass !== this.$vnode.data.staticClass) {

        this.staticClass = this.$vnode.data.staticClass;

      }

      if (this.className !== this.$vnode.data.class) {

        this.className = this.$vnode.data.class;

      }

    },
  },
});

</script>

<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
.sf-modal {
  --modal-index: 70;
  --overlay-z-index: 70;

  &__container {
    position: fixed;
    top: var(--modal-top, 0);
    right: var(--modal-right, 0);
    bottom: var(--modal-bottom, 0);
    left: var(--modal-left, 0);
    z-index: var(--modal-index, 1);
    transform: var(--modal-transform);
    display: flex;
    flex-direction: var(--modal-flex-direction, column);
    align-content: space-between;
    box-sizing: border-box;
    height: var(--modal-height);
    border: var(--modal-border);
    max-height: var(--modal-max-height);

    &::-webkit-scrollbar {
      width: 0;
    }
  }

  @include breakpoints.for-desktop {
    --modal-width: 29.375rem;
    --modal-top: 50%;
    --modal-left: 50%;
    --modal-bottom: none;
    --modal-right: none;
    --modal-transform: translate3d(-50%, -50%, 0);
    --modal-height: auto;
    --modal-max-height: 90%;
  }
}
</style>
